import React, { useState, useEffect } from "react";
import { Button, Modal, Select, Input, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getUserProfileQuestions, updateUserProfileQuestions } from "../../api/users";
import { getUser } from "../../store/slices/authSlice";
import { getCookie, setCookie } from "../../utils/cookieHandler";

// A skip is remembered in a cookie for 2 weeks (Tudor), so the user isn't
// re-nagged. The cookie is armed as soon as the modal is shown, so a plain
// refresh / tab-close also counts as "seen" — not only the explicit X.
const SKIP_COOKIE = "department_modal_skipped";
const SKIP_COOKIE_DAYS = 14;

// The show/hide decision is made ONCE per page-load, BEFORE the cookie is armed,
// and cached at module scope. Remounts (React StrictMode dev double-mount, a
// parent re-mounting) then reuse it instead of reading the cookie we just armed
// and wrongly hiding the modal. A full reload resets the module → re-reads.
let hiddenByCookie;
const shouldStayHidden = () => {
  if (hiddenByCookie === undefined) {
    hiddenByCookie =
      typeof document !== "undefined" && getCookie(SKIP_COOKIE) === "true";
  }
  return hiddenByCookie;
};

// Fallbacks used only if GET /user/profile-questions fails. Values MUST match the
// BE option strings exactly (en-dashes, € signs) — BE validates the answer against
// these exact strings, so they double as both the option value and its label.
const DEPARTMENTS_FALLBACK = [
  "Marketing/Brand", "Events", "People/HR", "Sales", "Partnerships/Channel",
  "Customer Success", "Procurement", "Operations/Office Management",
  "Executive/Founder/Leadership", "Community", "Other",
];
const COMPANY_SIZE_FALLBACK = [
  "1–10 employees", "11–50 employees", "51–200 employees", "201–500 employees",
  "501–1,000 employees", "1,001–5,000 employees", "5,000+ employees",
  "I don't know", "I prefer not to say",
];
const MERCH_SPEND_FALLBACK = [
  "Less than €5,000", "€5,000–€10,000", "€10,001–€25,000", "€25,001–€50,000",
  "€50,000–€100,000", "€100,000–€250,000", "€250,001–€500,000",
  "More than €500,000", "I don't know", "I prefer not to say",
];

const toOptions = (list) => (list || []).map((v) => ({ value: v, label: v }));

// A user still owes the survey if ANY of the three answers is missing. Drives
// both the ProtectedRoute gate and which fields the modal renders. BE exposes
// company_employee_count / promo_merch_spend on the user payload (UserResource).
export const needsProfileSurvey = (user) =>
  !user?.department || !user?.company_employee_count || !user?.promo_merch_spend;

// The survey modal is actually ON SCREEN only when the user still owes answers AND
// they haven't skipped/seen it (skip is remembered in the SKIP_COOKIE, not
// sessionStorage). Other surfaces — e.g. the onboarding tour — use this to wait
// behind the modal without duplicating the cookie logic.
export const isProfileSurveyOpen = (user) =>
  needsProfileSurvey(user) && !shouldStayHidden();

// Profile survey modal. It only renders the answers the user is still missing
// (someone who already has a department sees just the two enrichment questions),
// and every field it renders is required.
export const DepartmentModal = ({ isOpen }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  // Ask only for what's missing.
  const showDepartment = !user?.department;
  const showCompany = !user?.company_employee_count;
  const showMerch = !user?.promo_merch_spend;

  const [departments, setDepartments] = useState(DEPARTMENTS_FALLBACK);
  const [companySizes, setCompanySizes] = useState(COMPANY_SIZE_FALLBACK);
  const [merchSpends, setMerchSpends] = useState(MERCH_SPEND_FALLBACK);
  const [department, setDepartment] = useState(undefined);
  const [otherText, setOtherText] = useState("");
  const [companyEmployeeCount, setCompanyEmployeeCount] = useState(undefined);
  const [promoMerchSpend, setPromoMerchSpend] = useState(undefined);
  const [saved, setSaved] = useState(shouldStayHidden);
  const [loading, setLoading] = useState(false);
  const [showClose, setShowClose] = useState(false);
  // Flip on the first Save attempt so required (shown) fields show an inline error.
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowClose(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Arm the 2-week cookie once the modal is actually shown, so a plain refresh /
  // tab-close (not only the explicit X) stops it re-appearing. Uses the cached
  // decision, so arming the cookie never hides the currently-open modal.
  useEffect(() => {
    if (isOpen && !shouldStayHidden()) {
      setCookie(SKIP_COOKIE, "true", SKIP_COOKIE_DAYS);
    }
  }, []);

  const handleSkip = () => {
    setCookie(SKIP_COOKIE, "true", SKIP_COOKIE_DAYS);
    hiddenByCookie = true; // stay hidden for the rest of this page-load too
    setSaved(true);
  };

  useEffect(() => {
    getUserProfileQuestions()
      .then((res) => {
        const data = res?.data || res || {};
        if (Array.isArray(data.departments) && data.departments.length)
          setDepartments(data.departments);
        if (Array.isArray(data.company_employee_counts) && data.company_employee_counts.length)
          setCompanySizes(data.company_employee_counts);
        if (Array.isArray(data.promo_merch_spends) && data.promo_merch_spends.length)
          setMerchSpends(data.promo_merch_spends);
      })
      .catch(() => {});
  }, []);

  const isOther = department === "Other";

  // Every field the modal renders is required.
  const canSave =
    (!showDepartment || (!!department && (!isOther || otherText.trim().length > 0))) &&
    (!showCompany || !!companyEmployeeCount) &&
    (!showMerch || !!promoMerchSpend);

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setLoading(true);
      const payload = {};
      if (showDepartment) {
        payload.department = department;
        if (isOther) payload.department_other = otherText.trim();
      }
      if (showCompany) payload.company_employee_count = companyEmployeeCount;
      if (showMerch) payload.promo_merch_spend = promoMerchSpend;
      await updateUserProfileQuestions(payload);
      setSaved(true);
      dispatch(getUser());
      notification.success({ message: t('department.saveSuccess') });
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || t('department.saveError'),
      });
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const departmentError = showErrors && showDepartment && !department;
  const otherError = showErrors && showDepartment && isOther && !otherText.trim();
  const companyError = showErrors && showCompany && !companyEmployeeCount;
  const merchError = showErrors && showMerch && !promoMerchSpend;

  // Keep the button clickable; on an invalid click reveal the inline errors
  // instead of leaving the user with a silently-disabled button.
  const handleSaveClick = () => {
    if (!canSave) {
      setShowErrors(true);
      return;
    }
    handleSave();
  };

  return (
    <Modal
      className="department-modal"
      closable={showClose}
      title={<h3 className="text-center mb-0">{t('department.modalTitle')}</h3>}
      open={isOpen && !saved}
      onCancel={handleSkip}
      maskClosable={false}
      keyboard={false}
      footer={null}
    >
      <p className="mt-8 mb-12 text-gray">{t('department.modalDescription')}</p>

      <div className="d-flex flex-column gap-12">
        {showDepartment && (
          <div>
            <p className="dm-label text-gray">{t('settings.profile.department')}</p>
            <Select
              className={departmentError ? "w-100-percent input-error" : "w-100-percent"}
              placeholder={t('settings.profile.departmentPlaceholder')}
              value={department}
              onChange={setDepartment}
              options={toOptions(departments)}
            />
            {departmentError && (
              <span className="text-error font-size-12 d-block dm-field-error">{t('catalog.wishlists.fieldRequired')}</span>
            )}
          </div>
        )}

        {showDepartment && isOther && (
          <div>
            <p className="dm-label text-gray">{t('settings.profile.departmentOther')}</p>
            <Input
              className={otherError ? "w-100-percent input-error" : "w-100-percent"}
              maxLength={100}
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
            />
            {otherError && (
              <span className="text-error font-size-12 d-block dm-field-error">{t('catalog.wishlists.fieldRequired')}</span>
            )}
          </div>
        )}

        {showCompany && (
          <div>
            <p className="dm-label text-gray">{t('department.companySizeQuestion')}</p>
            <Select
              className={companyError ? "w-100-percent input-error" : "w-100-percent"}
              placeholder={t('department.selectOption')}
              value={companyEmployeeCount}
              onChange={setCompanyEmployeeCount}
              options={toOptions(companySizes)}
            />
            {companyError && (
              <span className="text-error font-size-12 d-block dm-field-error">{t('catalog.wishlists.fieldRequired')}</span>
            )}
          </div>
        )}

        {showMerch && (
          <div>
            <p className="dm-label text-gray">{t('department.merchSpendQuestion')}</p>
            <Select
              className={merchError ? "w-100-percent input-error" : "w-100-percent"}
              placeholder={t('department.selectOption')}
              value={promoMerchSpend}
              onChange={setPromoMerchSpend}
              options={toOptions(merchSpends)}
            />
            {merchError && (
              <span className="text-error font-size-12 d-block dm-field-error">{t('catalog.wishlists.fieldRequired')}</span>
            )}
          </div>
        )}
      </div>

      <Button
        block
        className="btn-primary mt-32"
        loading={loading}
        onClick={handleSaveClick}
      >
        {t('department.saveContinue')}
      </Button>
    </Modal>
  );
};

export default DepartmentModal;
